import { XCsrfHeaderName } from '@app/utils/fetcher';
import { valibotResolver } from '@hookform/resolvers/valibot';
import useConfiguration from '@store/hooks/useConfiguration';
import { CustomerType } from '@store/types';
import ky from 'ky';
import type { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CheckoutFormSchema as schema, type CheckoutFormSchema } from './schema';
import type { ICheckoutCustomer } from './types';
import React from 'react';

interface IProps {
  customer: ICheckoutCustomer;
  shippingMethodId?: string;
  paymentMethodId?: string;
  note?: string;

  nextStepUrl: string;
  setCheckoutWizardUrl: string;
}

export default function CheckoutForm({ children, ...props }: PropsWithChildren<IProps>) {
  const { xsrf } = useConfiguration();

  const form = useForm<CheckoutFormSchema>({
    resolver: valibotResolver(schema),
    defaultValues: {
      shippingMethodId: props.shippingMethodId ?? '',
      paymentMethodId: props.paymentMethodId ?? '',
      note: props.note ?? '',
      // Customer
      customer: {
        isCompany: props.customer.customerType === CustomerType.Company,
        customerName: props.customer.customerName ?? '',
        titlePrefix: props.customer.titlePrefix ?? '',
        firstName: props.customer.firstName ?? '',
        lastName: props.customer.lastName ?? '',
        titleSuffix: props.customer.titleSuffix ?? '',
        registrationNumber: props.customer.registrationNumber ?? '',
        vatNumber: props.customer.vatNumber ?? '',
        taxNumber: props.customer.taxNumber ?? '',
        phoneNumber: props.customer.phoneNumber ?? '',
        emailAddress: props.customer.emailAddress ?? '',
        billingStreet: props.customer.billingStreet ?? '',
        billingTown: props.customer.billingTown ?? '',
        billingPostCode: props.customer.billingPostCode ?? '',
        billingCountryCode: props.customer.billingCountryCode ?? '',
        // Shipping address
        differentShipping: !!props.customer.shippingAddress,
        shippingAddress: {
          addressId: props.customer.shippingAddress?.addressId ?? '',
          street: props.customer.shippingAddress?.street ?? '',
          town: props.customer.shippingAddress?.town ?? '',
          postCode: props.customer.shippingAddress?.postCode ?? '',
          countryCode: props.customer.shippingAddress?.countryCode ?? '',
        },
      },
    },
  });

  const onSubmit = async (data: CheckoutFormSchema) => {
    const { paymentMethodId, shippingMethodId, note, customer } = data;

    const json = {
      paymentMethodId: paymentMethodId,
      shippingMethodId: shippingMethodId,
      note: note,
      customer: {
        customerType: customer.isCompany ? CustomerType.Company : CustomerType.Citizen,
        ...(customer.isCompany
          ? {
              customerName: customer.customerName,
              registrationNumber: customer.registrationNumber,
              vatNumber: customer.vatNumber,
              taxNumber: customer.taxNumber,
            }
          : {}),
        titlePrefix: customer.titlePrefix,
        firstName: customer.firstName,
        lastName: customer.lastName,
        titleSuffix: customer.titleSuffix,
        phoneNumber: customer.phoneNumber,
        emailAddress: customer.emailAddress,
        billingStreet: customer.billingStreet,
        billingTown: customer.billingTown,
        billingPostCode: customer.billingPostCode,
        billingCountryCode: customer.billingCountryCode,
        shippingAddress: customer.differentShipping ? customer.shippingAddress : null,
      },
    };
    const response = await ky.post(props.setCheckoutWizardUrl, {
      json,
      headers: { [XCsrfHeaderName]: xsrf },
      throwHttpErrors: false,
    });

    if (response.ok) {
      location.href = props.nextStepUrl;
    }
  };

  React.useEffect(() => {
    console.debug(form.formState)
  }, [form.formState]);

  return (
    <FormProvider {...form}>
      <form className="grid grid-cols-1" onSubmit={form.handleSubmit(onSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
}
